// DTE › Formato de factura (pedido de Rodrigo, 2026-10-14): la factura impresa sale con el diseño de la
// de Random y la empresa agrega lo suyo en fichas ordenadas —correo, teléfono, sucursales, cuentas para
// transferir y una nota—, con la factura de ejemplo al lado que se actualiza mientras se escribe. No
// toca nada ante el SII (no va al XML ni al timbre).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, pedirArchivo } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { useSesion } from '../sesion/SesionContexto';
import { LogoEmisor, type DatosEmisor } from './LogoEmisor';

interface Sucursal {
  nombre: string;
  direccion: string;
  comuna: string;
  telefono: string;
}
interface Cuenta {
  banco: string;
  tipo: string;
  numero: string;
  titular: string;
  rut: string;
  correo: string;
}
interface Datos {
  correo: string;
  telefono: string;
  sucursales: Sucursal[];
  cuentas: Cuenta[];
  nota: string;
}
interface Formato {
  datos: Datos;
  version: number;
  tieneLogo?: boolean;
  emisor?: DatosEmisor | null;
}

const VACIO: Datos = { correo: '', telefono: '', sucursales: [], cuentas: [], nota: '' };
const SUCURSAL: Sucursal = { nombre: '', direccion: '', comuna: '', telefono: '' };
const CUENTA: Cuenta = {
  banco: '',
  tipo: 'Cuenta corriente',
  numero: '',
  titular: '',
  rut: '',
  correo: '',
};
const TIPOS = ['Cuenta corriente', 'Cuenta vista', 'Cuenta de ahorro', 'Cuenta RUT'];

export function FormatoFactura() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const editable = puede('dte.emisor.administrar');
  const q = useQuery({
    queryKey: ['dte', 'formato-impresion', empresaId],
    queryFn: () => api.get<Formato>(`/dte/formato-impresion?empresaId=${empresaId}`),
  });
  const [d, setD] = useState<Datos>(VACIO);
  useEffect(() => {
    if (q.data) setD({ ...VACIO, ...q.data.datos });
  }, [q.data]);
  const guardar = useMutation({
    mutationFn: () => api.put('/dte/formato-impresion', { empresaId, ...d }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dte', 'formato-impresion', empresaId] }),
  });
  const cambio = !!q.data && JSON.stringify(d) !== JSON.stringify({ ...VACIO, ...q.data.datos });

  // Vista previa: se pide al dejar de escribir (medio segundo) y se muestra el PDF de ejemplo.
  const [previa, setPrevia] = useState<{ url: string | null; error: unknown }>({
    url: null,
    error: null,
  });
  const ultima = useRef<string | null>(null);
  const [refresco, setRefresco] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(async () => {
      try {
        const b = await pedirArchivo('POST', '/dte/formato-impresion/vista-previa', {
          cuerpo: { empresaId, ...d },
        });
        if (typeof URL.createObjectURL !== 'function') return;
        const url = URL.createObjectURL(b);
        if (ultima.current) URL.revokeObjectURL(ultima.current);
        ultima.current = url;
        setPrevia({ url, error: null });
      } catch (error) {
        setPrevia((p) => ({ ...p, error }));
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [d, empresaId, refresco]);

  const campo = (k: 'correo' | 'telefono' | 'nota') => (v: string) => setD({ ...d, [k]: v });
  const sucursal = (i: number, k: keyof Sucursal, v: string) =>
    setD({ ...d, sucursales: d.sucursales.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const cuenta = (i: number, k: keyof Cuenta, v: string) =>
    setD({ ...d, cuentas: d.cuentas.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });

  return (
    <div className="pantalla formato-factura" data-pantalla="formato-factura">
      <header className="encabezado-entidad">
        <span className="ruta-migas">DTE › Impresión</span>
        <h2>Formato de factura</h2>
      </header>
      <div className="formato-cuerpo">
        <div className="formato-fichas">
          {q.isError && <PanelError error={q.error} />}
          {q.data && (
            <LogoEmisor
              empresaId={empresaId}
              editable={editable}
              tieneLogo={!!q.data.tieneLogo}
              emisor={q.data.emisor ?? null}
              alCambiar={() => setRefresco((x) => x + 1)}
            />
          )}
          <fieldset>
            <legend>Contacto (arriba, bajo el nombre de la empresa)</legend>
            <div className="formato-fila">
              <label className="campo">
                <span className="etiqueta">Correo</span>
                <input
                  aria-label="Correo"
                  disabled={!editable}
                  maxLength={60}
                  placeholder="gerencia@madeci.cl"
                  value={d.correo}
                  onChange={(e) => campo('correo')(e.target.value)}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Teléfono</span>
                <input
                  aria-label="Teléfono"
                  disabled={!editable}
                  maxLength={40}
                  placeholder="600 612 9877"
                  value={d.telefono}
                  onChange={(e) => campo('telefono')(e.target.value)}
                />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Sucursales (arriba) · hasta 4</legend>
            {d.sucursales.map((s, i) => (
              <div key={i} className="formato-fila" role="group" aria-label={`Sucursal ${i + 1}`}>
                <label className="campo">
                  <span className="etiqueta">Nombre</span>
                  <input
                    aria-label={`Nombre de la sucursal ${i + 1}`}
                    disabled={!editable}
                    maxLength={40}
                    placeholder="Sucursal Llolleo"
                    value={s.nombre}
                    onChange={(e) => sucursal(i, 'nombre', e.target.value)}
                  />
                </label>
                <label className="campo campo-ancho">
                  <span className="etiqueta">Dirección</span>
                  <input
                    aria-label={`Dirección de la sucursal ${i + 1}`}
                    disabled={!editable}
                    maxLength={70}
                    value={s.direccion}
                    onChange={(e) => sucursal(i, 'direccion', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Comuna</span>
                  <input
                    aria-label={`Comuna de la sucursal ${i + 1}`}
                    disabled={!editable}
                    maxLength={30}
                    value={s.comuna}
                    onChange={(e) => sucursal(i, 'comuna', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Teléfono</span>
                  <input
                    aria-label={`Teléfono de la sucursal ${i + 1}`}
                    disabled={!editable}
                    maxLength={30}
                    value={s.telefono}
                    onChange={(e) => sucursal(i, 'telefono', e.target.value)}
                  />
                </label>
                {editable && (
                  <button
                    type="button"
                    className="boton-mini"
                    onClick={() =>
                      setD({ ...d, sucursales: d.sucursales.filter((_, j) => j !== i) })
                    }
                  >
                    Quitar
                  </button>
                )}
              </div>
            ))}
            {editable && d.sucursales.length < 4 && (
              <button
                type="button"
                onClick={() => setD({ ...d, sucursales: [...d.sucursales, { ...SUCURSAL }] })}
              >
                + Agregar sucursal
              </button>
            )}
          </fieldset>

          <fieldset>
            <legend>Cuentas para transferir (abajo, en Observaciones) · hasta 4</legend>
            {d.cuentas.map((c, i) => (
              <div key={i} className="formato-fila" role="group" aria-label={`Cuenta ${i + 1}`}>
                <label className="campo">
                  <span className="etiqueta">Banco</span>
                  <input
                    aria-label={`Banco de la cuenta ${i + 1}`}
                    disabled={!editable}
                    maxLength={40}
                    placeholder="Banco Santander"
                    value={c.banco}
                    onChange={(e) => cuenta(i, 'banco', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Tipo</span>
                  <select
                    aria-label={`Tipo de la cuenta ${i + 1}`}
                    disabled={!editable}
                    value={c.tipo}
                    onChange={(e) => cuenta(i, 'tipo', e.target.value)}
                  >
                    {TIPOS.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="campo">
                  <span className="etiqueta">Número</span>
                  <input
                    aria-label={`Número de la cuenta ${i + 1}`}
                    disabled={!editable}
                    maxLength={30}
                    value={c.numero}
                    onChange={(e) => cuenta(i, 'numero', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Titular</span>
                  <input
                    aria-label={`Titular de la cuenta ${i + 1}`}
                    disabled={!editable}
                    maxLength={60}
                    value={c.titular}
                    onChange={(e) => cuenta(i, 'titular', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">RUT</span>
                  <input
                    aria-label={`RUT del titular ${i + 1}`}
                    disabled={!editable}
                    maxLength={14}
                    value={c.rut}
                    onChange={(e) => cuenta(i, 'rut', e.target.value)}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Correo del comprobante</span>
                  <input
                    aria-label={`Correo de la cuenta ${i + 1}`}
                    disabled={!editable}
                    maxLength={60}
                    value={c.correo}
                    onChange={(e) => cuenta(i, 'correo', e.target.value)}
                  />
                </label>
                {editable && (
                  <button
                    type="button"
                    className="boton-mini"
                    onClick={() => setD({ ...d, cuentas: d.cuentas.filter((_, j) => j !== i) })}
                  >
                    Quitar
                  </button>
                )}
              </div>
            ))}
            {editable && d.cuentas.length < 4 && (
              <button
                type="button"
                onClick={() =>
                  setD({
                    ...d,
                    cuentas: [...d.cuentas, { ...CUENTA, titular: empresa!.razon_social ?? '' }],
                  })
                }
              >
                + Agregar cuenta
              </button>
            )}
          </fieldset>

          <fieldset>
            <legend>Nota al pie (opcional)</legend>
            <label className="campo campo-ancho">
              <span className="etiqueta">Una línea: horarios, condiciones…</span>
              <input
                aria-label="Nota al pie"
                disabled={!editable}
                maxLength={150}
                value={d.nota}
                onChange={(e) => campo('nota')(e.target.value)}
              />
            </label>
          </fieldset>

          {guardar.isError && <PanelError error={guardar.error} />}
          {guardar.isSuccess && !cambio && <p role="status">Guardado. Ya sale en las facturas.</p>}
          {editable && (
            <div className="acciones-ficha">
              <button
                type="button"
                className="primario"
                disabled={!cambio || guardar.isPending}
                onClick={() => guardar.mutate()}
              >
                Guardar formato
              </button>
            </div>
          )}
          <p className="apagado">
            No cambia nada ante el SII. Se ve también al reimprimir facturas ya emitidas.
          </p>
        </div>
        <div className="formato-previa">
          <span className="etiqueta">Vista previa (factura de ejemplo)</span>
          {previa.error ? <PanelError error={previa.error} /> : null}
          {previa.url ? (
            <iframe title="Vista previa de la factura" src={`${previa.url}#toolbar=0&view=FitH`} />
          ) : (
            <p className="apagado">Preparando la vista previa…</p>
          )}
        </div>
      </div>
    </div>
  );
}
